// Shared CallRail (Dynamic Number Insertion) loader/rescanner.
// Single source of truth so every entry point (gatsby-browser's route hook,
// and any component that mounts phone numbers after its own async gate,
// e.g. ProductTemplate's post-load render) swaps against the same script
// instance instead of loading/scheduling their own copies.

const CALLRAIL_SCRIPT_ID = "callrail-script"
const CALLRAIL_SCRIPT_SRC =
  "https://cdn.callrail.com/companies/152719966/015a9621411c8b5ca23f/12/swap.js"

let callRailLoadPromise

/**
 * CallRail is a singleton for the lifetime of the Gatsby browser session.
 * Keeping one script/runtime preserves the visitor's assigned tracking number.
 */
export const loadCallRail = () => {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return Promise.resolve(null)
  }

  if (window.CallTrk && typeof window.CallTrk.swap === "function") {
    return Promise.resolve(window.CallTrk)
  }

  if (callRailLoadPromise) {
    return callRailLoadPromise
  }

  callRailLoadPromise = new Promise((resolve, reject) => {
    const existingScript = document.getElementById(CALLRAIL_SCRIPT_ID)
    const script = existingScript || document.createElement("script")

    const handleLoad = () => resolve(window.CallTrk || null)
    const handleError = () => {
      callRailLoadPromise = undefined
      reject(new Error("CallRail swap.js failed to load"))
    }

    script.addEventListener("load", handleLoad, { once: true })
    script.addEventListener("error", handleError, { once: true })

    if (!existingScript) {
      script.id = CALLRAIL_SCRIPT_ID
      script.src = CALLRAIL_SCRIPT_SRC
      script.async = true
      document.body.appendChild(script)
    }
  })

  return callRailLoadPromise
}

/**
 * React can mount/replace DOM after CallRail already swapped a previous pass
 * (e.g. Gatsby SPA navigation, or a component that renders its real content
 * behind an async/client-only gate). CallRail documents CallTrk.swap(node)
 * for rescanning content added after the initial scan.
 */
export const rescanCallRail = () => {
  return loadCallRail()
    .then(callTrk => {
      if (!callTrk || typeof callTrk.swap !== "function") return

      const gatsbyRoot =
        document.getElementById("gatsby-focus-wrapper") ||
        document.getElementById("___gatsby") ||
        document.body

      callTrk.swap(gatsbyRoot)
    })
    .catch(error => {
      console.warn(error.message)
    })
}
